import { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import {
    DocumentElement, twipsToPx,
    IParagraphBox, ITextBox,
} from '@tencent/alloy-editor';
import {
    isStringVisible,
} from '@src/slide/application/service/slide-rating-and-improve/text/calculate/text-box-calculate';
import isEqualWith from 'lodash/isEqualWith';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import { IConnectorModel } from '@src/slide/domain/ui-model/element-modeles/Interface/connector-model.interface';
import { LineType } from '@src/slide/infrastructure/module/dependent/line-info';
import {
    IElementNode, ITextBlockParentBoxNode, ITextParagraphBoxNode,
} from '@src/slide/application/service/slide-content-enumerator/enumerator-types.interface';
import { FONT_SCALE } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import {
    getBuiltInBulletConfig,
} from '@src/slide/domain/ui-model/element-modeles/property/text/utils/character-utils/built-in-bullet';


export enum VisualBlockType {
    ShapeWithTextAndFillOrStroke = 'shapeWithTextAndFillOrStroke',
    OnlyTextShape = 'onlyTextShape',
    Element = 'element',
}

export enum VisualBlockFeatureType {
    Frame,
    Fill,
    Border,
    Effect,
    Text,
}

/**
 * 页面块
 */
export class VisualBlock {
    public rect: IRect;
    /**
     * ITextBlockParentBoxNode表示是一个有文本并且（有背景或边框）的Shape
     * ITextParagraphBoxNode表示是一个有文本并且无填充和边框的Shape
     * IElementNode表示是Shape之外的其他元素或者没有文字的Shape
     */
    public info?: IElementNode | ITextParagraphBoxNode | ITextBlockParentBoxNode;
    public degreeOfCoherence: number;
    public children?: Array<VisualBlock>;
    // 在list里的先后顺序，用于排序使用
    public listIndex?: number;

    private _hasText?: boolean;
    // 第一个可见的文字的字号
    private _firstFontSize?: number;
    private _firstFontStyle?: any;
    private _blockType?: VisualBlockType;
    // 是否是水平或者垂直线
    private _isLine?: boolean;

    public constructor(rect: IRect, info?: IElementNode | ITextParagraphBoxNode | ITextBlockParentBoxNode) {
        this.rect = rect;
        this.info = info;
    }

    public blockType(): VisualBlockType {
        if (this._blockType !== undefined) return this._blockType;
        if (!this.info) return VisualBlockType.Element;
        if ((this.info as ITextParagraphBoxNode).paraBox) {
            this._blockType = VisualBlockType.OnlyTextShape;
        } else if ((this.info as ITextBlockParentBoxNode).blockBox) {
            this._blockType = VisualBlockType.ShapeWithTextAndFillOrStroke;
        } else {
            this._blockType = VisualBlockType.Element;
        }
        return this._blockType;
    }

    public hasText(): boolean {
        if (this._hasText !== undefined) return this._hasText;
        this._hasText = !!(this.info as ITextParagraphBoxNode).paraBox
            || !!(this.info as ITextBlockParentBoxNode).blockBox;
        return this._hasText;
    }

    // 是否是水平或者垂直线
    public isLine(): boolean {
        if (this._isLine !== undefined) return this._isLine;
        if (this.blockType() === VisualBlockType.Element) {
            const elementNode = this.info as IElementNode;
            if (elementNode.element.type === UIModelType.CONNECTOR) {
                const connectorModel = (elementNode.element as IConnectorModel);
                if (connectorModel.getFrame().getFrameInfo().height <= 0
                    || connectorModel.getFrame().getFrameInfo().width <= 0) {
                    const lineType = connectorModel.getGraphic().getGraphicLinInfo()?.bgLine;
                    if (lineType === LineType.LINE || lineType === LineType.STRAIGNTCONNECTOR1) {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    public getTextDocument(): DocumentElement | undefined {
        if (!this.hasText()) {
            return undefined;
        }
        if ((this.info as ITextParagraphBoxNode).paraBox) {
            return (this.info as ITextParagraphBoxNode).textDocumentElement;
        }
        if ((this.info as ITextBlockParentBoxNode).blockBox) {
            return (this.info as ITextBlockParentBoxNode).textDocumentElement;
        }
    }

    public getText(): string | undefined {
        if (!this.hasText()) {
            return undefined;
        }
        let text = '';
        if ((this.info as ITextParagraphBoxNode).paraBox) {
            const { paraBox } = this.info as ITextParagraphBoxNode;
            paraBox.children.forEach((lineBox) => {
                lineBox.children.forEach((textBox: ITextBox) => {
                    // @ts-ignore
                    const isListMark = !!textBox.isListMark;
                    if (isListMark) {
                        text += this.getTextFromListMarkBox(textBox);
                    } else {
                        text += textBox.text;
                    }
                });
            });
        }
        if ((this.info as ITextBlockParentBoxNode).blockBox) {
            const { blockBox } = this.info as ITextBlockParentBoxNode;
            blockBox.children.forEach((paraBox) => {
                (paraBox as IParagraphBox).children.forEach((lineBox) => {
                    lineBox.children.forEach((textBox: ITextBox) => {
                        // @ts-ignore
                        const isListMark = !!textBox.isListMark;
                        if (isListMark) {
                            text += this.getTextFromListMarkBox(textBox);
                        } else {
                            text += textBox.text;
                        }
                    });
                });
                text += '';
            });
        }
        return text;
    }

    public getFrame(): IFrame | undefined {
        return this.info?.element.getFrame().frameOnSlide();
    }

    /**
     * 第一个可显的字体大小
     */
    // eslint-disable-next-line complexity
    public getFirstFontSize(): number {
        if (this._firstFontSize !== undefined) {
            return this._firstFontSize;
        }
        if (!this.hasText()) {
            this._firstFontSize = -1;
            return this._firstFontSize;
        }
        if ((this.info as ITextParagraphBoxNode).paraBox) {
            const { paraBox } = this.info as ITextParagraphBoxNode;
            for (const lineBox of paraBox.children) {
                for (const textBox of lineBox.children) {
                    if (isStringVisible((textBox as ITextBox).text)) {
                        this._firstFontSize = twipsToPx(textBox.textStyle.fontSize) / FONT_SCALE;
                        return this._firstFontSize;
                    }
                }
            }
        }
        if ((this.info as ITextBlockParentBoxNode).blockBox) {
            const { blockBox } = this.info as ITextBlockParentBoxNode;
            for (const paraBox of blockBox.children) {
                for (const lineBox of (paraBox as IParagraphBox).children) {
                    for (const textBox of lineBox.children) {
                        if (isStringVisible((textBox as ITextBox).text)) {
                            this._firstFontSize = twipsToPx(textBox.textStyle.fontSize) / FONT_SCALE;
                            return this._firstFontSize;
                        }
                    }
                }
            }
        }
        // 理论上不会走到这里
        this._firstFontSize = -1;
        return this._firstFontSize;
    }

    // eslint-disable-next-line complexity
    public getFirstFontStyle(): any {
        if (this._firstFontStyle !== undefined) {
            return this._firstFontStyle;
        }
        if (!this.hasText()) {
            this._firstFontStyle = undefined;
            return this._firstFontStyle;
        }
        if ((this.info as ITextParagraphBoxNode).paraBox) {
            const { paraBox } = this.info as ITextParagraphBoxNode;
            for (const lineBox of paraBox.children) {
                for (const textBox of lineBox.children) {
                    if (isStringVisible((textBox as ITextBox).text)) {
                        this._firstFontStyle = textBox.textStyle;
                        return this._firstFontStyle;
                    }
                }
            }
        }
        if ((this.info as ITextBlockParentBoxNode).blockBox) {
            const { blockBox } = this.info as ITextBlockParentBoxNode;
            for (const paraBox of blockBox.children) {
                for (const lineBox of (paraBox as IParagraphBox).children) {
                    for (const textBox of lineBox.children) {
                        if (isStringVisible((textBox as ITextBox).text)) {
                            this._firstFontStyle = textBox.textStyle;
                            return this._firstFontStyle;
                        }
                    }
                }
            }
        }
        // 理论上不会走到这里
        this._firstFontStyle = undefined;
        return this._firstFontStyle;
    }

    /**
     * 判断和另一个块是否相似
     * @param other
     */
    // eslint-disable-next-line complexity
    public isSimilarToOther(
        other: VisualBlock,
        exceptFeatureTypes?: Array<VisualBlockFeatureType>,
        centerX?: number,
    ): boolean {
        if (this.blockType() !== other.blockType()) return false;
        if (this.blockType() === VisualBlockType.Element) {
            const element1 = (this.info as IElementNode).element;
            const element2 = (other.info as IElementNode).element;
            if (element1.type !== element2.type) {
                return false;
            }
            // 尺寸大小相差比较少
            if (!exceptFeatureTypes?.includes(VisualBlockFeatureType.Frame)
                && !isEqualWith(
                    element1.getFrame().frameOnSlide(),
                    element2.getFrame().frameOnSlide(),
                    this.customizer,
                )) {
                return false;
            }
            if (!exceptFeatureTypes?.includes(VisualBlockFeatureType.Fill)
                && element1.getBackgroundFill().getShownFillType()
                   !== element2.getBackgroundFill().getShownFillType()) {
                return false;
            }
            if (!exceptFeatureTypes?.includes(VisualBlockFeatureType.Border)
                && element1.getBorder().getBorderType() !== element2.getBorder().getBorderType()) {
                return false;
            }
        }
        if (this.blockType() === VisualBlockType.ShapeWithTextAndFillOrStroke) {
            const textBlockNode1 = (this.info as ITextBlockParentBoxNode);
            const textBlockNode2 = (other.info as ITextBlockParentBoxNode);
            if (
                !exceptFeatureTypes?.includes(VisualBlockFeatureType.Fill)
                && !isEqualWith(
                    textBlockNode1.element.getBackgroundFill().getFillInfo(),
                    textBlockNode2.element.getBackgroundFill().getFillInfo(),
                )) {
                return false;
            }
            if (!exceptFeatureTypes?.includes(VisualBlockFeatureType.Border)
                && !isEqualWith(
                    textBlockNode1.element.getBorder().getBorderInfo(),
                    textBlockNode2.element.getBorder().getBorderInfo(),
                )) {
                return false;
            }
            if (!exceptFeatureTypes?.includes(VisualBlockFeatureType.Effect)
                && !isEqualWith(
                    textBlockNode1.element.getEffect().getEffectCollection(),
                    textBlockNode2.element.getEffect().getEffectCollection(),
                )) {
                return false;
            }
        }
        if (this.blockType() === VisualBlockType.ShapeWithTextAndFillOrStroke
            || this.blockType() === VisualBlockType.OnlyTextShape
        ) {
            const textPara1 = (this.info as ITextParagraphBoxNode);
            const textPara2 = (other.info as ITextParagraphBoxNode);

            const frame1 = this.rect;
            const frame2 = other.rect;

            // 判断是否左对齐、上对齐或者中心对齐、或右对齐、下对齐
            const centerX1 = frame1.left + (frame1.width / 2);
            const centerX2 = frame2.left + (frame2.width / 2);
            const pageCenterX = centerX !== undefined ? centerX : (centerX1 + centerX2) / 2;
            if (!exceptFeatureTypes?.includes(VisualBlockFeatureType.Frame)
                && (Math.abs(frame1.left - frame2.left) > 80)
                && ( // 两个矩形居中接近，并且
                    Math.abs(centerX1 - centerX2) > 80
                    || Math.abs(centerX1 - pageCenterX) > 80
                    || Math.abs(centerX2 - pageCenterX) > 80
                )
                && (Math.abs((frame1.left + frame1.width)
                    - (frame2.left + frame2.width)) > 80)
            ) {
                return false;
            }
            if (!exceptFeatureTypes?.includes(VisualBlockFeatureType.Frame)
                && Math.abs(frame1.top - frame2.top) > 80
                && (Math.abs((frame1.top + (frame1.height / 2))
                    - (frame2.top + (frame2.height / 2))) > 80)
                && (Math.abs((frame1.top + frame1.height)
                    - (frame2.top + frame2.height)) > 80)
            ) {
                return false;
            }

            // 高度相差超过1倍的，不认为相似
            if (!exceptFeatureTypes?.includes(VisualBlockFeatureType.Frame)
                && Math.abs(frame1.height - frame2.height) > Math.min(frame1.height, frame2.height)
            ) {
                return false;
            }
            const textSize1 = textPara1.textDocumentElement.children?.[0].children?.[0].fontSize;
            const textSize2 = textPara2.textDocumentElement.children?.[0].children?.[0].fontSize;
            if (!exceptFeatureTypes?.includes(VisualBlockFeatureType.Text)
                && textSize1 !== undefined && textSize2 !== undefined
                && Math.abs(textSize2 - textSize1) > 4) {
                return false;
            }
        }
        return true;
    }

    /**
     * 和另一个块是否几乎相同
     * @param other
     */
    // eslint-disable-next-line complexity
    public isSameToOther(other: VisualBlock): boolean {
        if (this.blockType() !== other.blockType()) return false;
        if (this.blockType() === VisualBlockType.Element) {
            const element1 = (this.info as IElementNode).element;
            const element2 = (other.info as IElementNode).element;
            if (element1.type !== element2.type) {
                return false;
            }

            // 尺寸大小完全相同
            if (!isEqualWith(
                element1.getFrame().frameOnSlide(),
                element2.getFrame().frameOnSlide(),
            )) {
                return false;
            }
            if (element1.type === UIModelType.SHAPE) {
                if (element1.getBackgroundFill().getShownFillType()
                    !== element2.getBackgroundFill().getShownFillType()) {
                    return false;
                }
                if (element1.getBorder().getBorderType() !== element2.getBorder().getBorderType()) {
                    return false;
                }
            }
        }
        if (this.blockType() === VisualBlockType.ShapeWithTextAndFillOrStroke) {
            const textBlockNode1 = (this.info as ITextBlockParentBoxNode);
            const textBlockNode2 = (other.info as ITextBlockParentBoxNode);
            if (!isEqualWith(
                textBlockNode1.element.getBackgroundFill().getFillInfo(),
                textBlockNode2.element.getBackgroundFill().getFillInfo(),
            )) {
                return false;
            }
            if (!isEqualWith(
                textBlockNode1.element.getBorder().getBorderInfo(),
                textBlockNode2.element.getBorder().getBorderInfo(),
            )) {
                return false;
            }
            if (!isEqualWith(
                textBlockNode1.element.getEffect().getEffectCollection(),
                textBlockNode2.element.getEffect().getEffectCollection(),
            )) {
                return false;
            }
            if (!isEqualWith(
                textBlockNode1.element.getFrame().frameOnSlide(),
                textBlockNode2.element.getFrame().frameOnSlide(),
            )) {
                return false;
            }
        }
        if (this.blockType() === VisualBlockType.ShapeWithTextAndFillOrStroke
            || this.blockType() === VisualBlockType.OnlyTextShape
        ) {
            const textPara1 = (this.info as ITextParagraphBoxNode);
            const textPara2 = (other.info as ITextParagraphBoxNode);
            const frame1 = this.rect;
            const frame2 = other.rect;
            // 判断是否左对齐、上对齐或者中心对齐、或右对齐、下对齐
            if ((Math.abs(frame1.left - frame2.left) > 1)
                && (Math.abs((frame1.left + (frame1.width / 2))
                    - (frame2.left + (frame2.width / 2))) > 1)
                && (Math.abs((frame1.left + frame1.width)
                    - (frame2.left + frame2.width)) > 1)
            ) {
                return false;
            }
            if (Math.abs(frame1.top - frame2.top) > 1
                && (Math.abs((frame1.top + (frame1.height / 2))
                    - (frame2.top + (frame2.height / 2))) > 1)
                && (Math.abs((frame1.top + frame1.height)
                    - (frame2.top + frame2.height)) > 1)
            ) {
                return false;
            }

            const textRun1 = textPara1.textDocumentElement.children?.[0].children?.[0];
            const textRun2 = textPara2.textDocumentElement.children?.[0].children?.[0];
            if (textRun1.fontSize !== textRun2.fontSize
                || textRun1?.background
            ) {
                return false;
            }
        }
        return true;
    }

    // eslint-disable-next-line complexity
    private customizer(objValue, othValue, key, object, other, stack) {
        const keys = ['left', 'top', 'width', 'height'];
        if (keys.includes(key) && Math.abs(objValue - othValue) < 30) {
            return true;
        }
        if (key === 'text' || key === 'origin-data') {
            return true;
        }
        if (typeof(key) === 'string' && (
            key?.includes('fonts') || key?.includes('fontFamily')
            || key?.startsWith('origin') || key?.includes('lang')
        )) {
            return true;
        }

        const o1Keys = new Set(); const o2Keys = new Set();
        if (objValue) {
            Object.keys(objValue).forEach(key1 => o1Keys.add(key1));
        }
        if (othValue) {
            Object.keys(othValue).forEach(key1 => o2Keys.add(key1));
        }
        const otherNotHas = [...o1Keys].filter(x => !o2Keys.has(x));
        const objNotHas = [...o2Keys].filter(x => !o1Keys.has(x));
        if ((otherNotHas.length === 1 && objNotHas.length === 0 && otherNotHas[0] === 'origin-data')
            || (objNotHas.length === 1 && otherNotHas.length === 0 && objNotHas[0] === 'origin-data')
        ) {
            return true;
        }
    }

    private getTextFromListMarkBox(textBox: ITextBox): string {
        const originFont = textBox.textStyle.fontFamily;
        if (originFont.length <= 2) return textBox.text;
        if (originFont.startsWith('"') && originFont.endsWith('"')) {
            const fontFamily = originFont.substring(1, originFont.length - 1);
            const config = getBuiltInBulletConfig(textBox.text, fontFamily);
            if (!config) return textBox.text;
            return config.backup.char;
        }
        return textBox.text;
    }
}
